import React, {
  CSSProperties,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size,
} from "@floating-ui/dom";
import { DropdownProps, DropdownOption } from "./types";
import ChevronDownIcon from "../../assets/svg/chevron-down-icon";
import CheckIcon from "../../assets/svg/check-icon";
import { visibleScrollbar } from "../../utils/functions/get-visible-scrollbar";
import InfoBtnIcon from "../../assets/svg/info-btn-icon";
import { Tooltip } from "../Tooltip/Tooltip";

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  id,
  placeholder = "Select an option",
  disabled = false,
  className = "",
  selectedOptionContainerClassName = "",
  triggerClassName = "",
  triggerIconClassName = "",
  dropdownClassName = "",
  optionClassName = "",
  checkIconClassName = "",
  ariaLabel,
  ariaDescribedBy,
  showChevron = true,
  label,
  labelClassName = "",
  placeholderClassName = "",
  showTooltip = false,
  tooltipText = "",
  dropdownMenuTitle,
  customTriggerStyle,
  showLineSeparator = false,
  customTrigger,
  selectedOptionTextClassName = "",
  open,
  onOpenChange,
  menuPortalTarget,
  Icon,
  iconClassName = "",
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalIsOpen;

  const updateOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalIsOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const dropdownId = id || `dropdown-${Math.random().toString(36).slice(2, 9)}`;
  const listboxId = `${dropdownId}-listbox`;
  const triggerId = `${dropdownId}-trigger`;
  const defaultAriaDescribedBy = `${dropdownId}-description`;

  const selectedOption = options?.find((option) => option.value === value);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    updateOpen(!isOpen);
    setFocusedIndex(-1);
  }, [disabled, isOpen, updateOpen]);

  const handleOptionSelect = useCallback(
    (option: DropdownOption) => {
      if (!option.disabled) {
        onChange(option.value, option);
        updateOpen(false);
        setFocusedIndex(-1);
        triggerRef.current?.focus();
      }
    },
    [onChange, updateOpen]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case "Enter":
        case " ":
          event.preventDefault();
          if (isOpen && focusedIndex >= 0) {
            handleOptionSelect(options[focusedIndex]);
          } else {
            handleToggle();
          }
          break;
        case "Escape":
          if (isOpen) {
            updateOpen(false);
            setFocusedIndex(-1);
            triggerRef.current?.focus();
          }
          break;
        case "ArrowDown":
          event.preventDefault();
          if (!isOpen) {
            updateOpen(true);
            setFocusedIndex(0);
          } else {
            setFocusedIndex((prev) => {
              const nextIndex = prev < options?.length - 1 ? prev + 1 : 0;
              for (let i = nextIndex; i < options?.length; i++) {
                if (!options[i].disabled) return i;
              }
              for (let i = 0; i < nextIndex; i++) {
                if (!options[i].disabled) return i;
              }
              return prev;
            });
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          if (isOpen) {
            setFocusedIndex((prev) => {
              const nextIndex = prev > 0 ? prev - 1 : options?.length - 1;
              for (let i = nextIndex; i >= 0; i--) {
                if (!options[i].disabled) return i;
              }
              for (let i = options?.length - 1; i > nextIndex; i--) {
                if (!options[i].disabled) return i;
              }
              return prev;
            });
          }
          break;
        case "Home":
          event.preventDefault();
          if (isOpen) {
            const firstEnabledIndex = options.findIndex(
              (option) => !option.disabled
            );
            if (firstEnabledIndex !== -1) {
              setFocusedIndex(firstEnabledIndex);
            }
          }
          break;
        case "End":
          event.preventDefault();
          if (isOpen) {
            for (let i = options.length - 1; i >= 0; i--) {
              if (!options[i].disabled) {
                setFocusedIndex(i);
                break;
              }
            }
          }
          break;
      }
    },
    [
      disabled,
      isOpen,
      focusedIndex,
      options,
      handleToggle,
      handleOptionSelect,
      updateOpen,
    ]
  );

  const handleOverlayClick = useCallback(() => {
    updateOpen(false);
    setFocusedIndex(-1);
  }, [updateOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
      return;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const [portalMenuStyle, setPortalMenuStyle] = useState<CSSProperties>({});
  const hasPositionedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasPositionedRef.current = false;
      return;
    }
    const trigger = triggerRef.current;
    const floating = dropdownRef.current;
    if (!trigger || !floating) return;

    return autoUpdate(trigger, floating, () => {
      computePosition(trigger, floating, {
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [
          offset(2),
          flip({ padding: 8 }),
          shift({ padding: 0 }),
          size({
            apply({ availableHeight, rects, elements }) {
              elements.floating.style.maxHeight = `${Math.min(250, availableHeight - 8)}px`;
              elements.floating.style.width = `${rects.reference.width}px`;
            },
          }),
        ],
      }).then(({ x, y }) => {
        hasPositionedRef.current = true;
        setPortalMenuStyle({
          position: "fixed",
          top: y,
          left: x,
          visibility: "visible",
        });
      });
    });
  }, [isOpen]);

  const renderDropdown = () => {
    if (!isOpen) return null;

    const portalTarget =
      menuPortalTarget ?? document.fullscreenElement ?? document.body;
    const listboxStyle: CSSProperties = hasPositionedRef.current
      ? portalMenuStyle
      : { position: "fixed", visibility: "hidden" };

    return createPortal(
      <>
        <div
          className="fixed inset-0"
          style={{ zIndex: 9999998, pointerEvents: "auto" }}
          data-modal-ignore-outside
          onClick={handleOverlayClick}
          onWheel={(e) => e.preventDefault()}
          onTouchMove={(e) => e.preventDefault()}
        />
        <div
          ref={dropdownRef}
          id={listboxId}
          role="listbox"
          data-modal-ignore-outside
          aria-label="Options"
          aria-activedescendant={
            focusedIndex >= 0
              ? `${dropdownId}-option-${focusedIndex}`
              : undefined
          }
          style={{ ...listboxStyle, zIndex: 9999999, pointerEvents: "auto" }}
          className={`overflow-y-auto ${visibleScrollbar(
            "[&::-webkit-scrollbar]:w-[8px]"
          )} border border-border-gray dark:border-border-gray-dk bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] rounded-[8px] shadow-popper-blur-effect max-h-[250px] ${dropdownClassName}`}
          onWheel={(e) => e.stopPropagation()}
        >
          {dropdownMenuTitle && <div>{dropdownMenuTitle}</div>}
          {options?.length > 0 ? (
            options.map((option, index) => {
              const isSelected = option.value === value;
              const isFocused = focusedIndex === index;
              const isDisabled = option.disabled || false;

              return (
                <div
                  key={option.id || `${dropdownId}-option-${index}`}
                  id={`${dropdownId}-option-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isDisabled}
                  tabIndex={-1}
                  className={`
                    flex items-center justify-between cursor-pointer text-md
                    text-heading-text dark:text-heading-text-dk
                    last:border-b-0 transition-colors px-[14px] py-[10px] min-w-0
                    ${
                      isDisabled
                        ? "opacity-50 "
                        : isSelected
                          ? "bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk font-medium"
                          : isFocused
                            ? "bg-hover-effect dark:bg-hover-effect-dk"
                            : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                    }
                    ${optionClassName}
                  `}
                  onClick={() => handleOptionSelect(option)}
                  onMouseEnter={() => !isDisabled && setFocusedIndex(index)}
                  onMouseLeave={() => setFocusedIndex(-1)}
                >
                  <div className="overflow-hidden flex-1 min-w-0 pr-5 select-none">
                    {option.content}
                  </div>

                  {isSelected && (
                    <CheckIcon
                      className={`w-4 h-4 text-verba-blue dark:text-verba-blue-dk shrink-0 ${checkIconClassName}`}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className="px-3 py-2 text-md text-heading-text dark:text-heading-text-dk">
              No options available
            </div>
          )}
        </div>
      </>,
      portalTarget
    );
  };

  const renderTrigger = () => {
    if (customTrigger) {
      return customTrigger;
    }
    return (
      <div
        className={`flex items-center gap-[8px] justify-between w-full px-[8px] py-[7px] ${selectedOptionContainerClassName}`}
      >
        {Icon && <Icon className={`shrink-0 ${iconClassName}`} />}
        <div
          className={`text-sm font-medium  ${
            selectedOption
              ? "text-heading-text dark:text-heading-text-dk"
              : placeholderClassName ||
                "text-heading-text dark:text-heading-text-dk"
          } ${selectedOptionTextClassName}`}
        >
          {selectedOption
            ? selectedOption?.selectedOptionContent
              ? selectedOption?.selectedOptionContent
              : selectedOption.content
            : placeholder}
        </div>
        {showLineSeparator && (
          <div className="shrink-0 bg-fixed-white dark:bg-fixed-white-dk w-px h-[10px]"></div>
        )}
        {showChevron && (
          <ChevronDownIcon
            className={`w-[8px] h-[8px] shrink-0 text-heading-text dark:text-heading-text-dk transition-transform duration-400 ${triggerIconClassName} ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </div>
    );
  };

  return (
    <div className={`${className}`}>
      {label && (
        <label
          htmlFor={triggerId}
          className={`flex items-center gap-[2px] font-normal text-verba-field-label dark:text-verba-field-label-dk mb-1 ${labelClassName}`}
        >
          <div>{label}</div>
          {showTooltip && (
            <Tooltip content={tooltipText}>
              <button>
                <InfoBtnIcon className="text-gray-placeholder dark:text-gray-placeholder-dk w-[18px] h-[18px]" />
              </button>
            </Tooltip>
          )}
        </label>
      )}

      <div
        ref={triggerRef}
        id={triggerId}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={ariaLabel || "Select an option"}
        aria-describedby={ariaDescribedBy || defaultAriaDescribedBy}
        tabIndex={disabled ? -1 : 0}
        className={`select-none cursor-pointer flex items-center rounded-[6px] border border-border-gray dark:border-border-gray-dk bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] transition-colors focus:outline-none
          ${disabled && "opacity-30 "}
          ${triggerClassName}
        `}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        style={customTriggerStyle}
      >
        {renderTrigger()}
      </div>
      {renderDropdown()}
    </div>
  );
};

export default Dropdown;
